import { create } from 'zustand';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
}

export interface AdminProfile {
  uid: string;
  email: string;
  displayName: string;
}

interface AuthState {
  user: AuthUser | null;
  adminProfile: AdminProfile | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  syncFromFirebaseUser: (firebaseUser: FirebaseUser | null) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AUTH_ERROR_MESSAGE = '管理者としてログインできませんでした';

const mapFirebaseUser = (firebaseUser: FirebaseUser): AuthUser => ({
  uid: firebaseUser.uid,
  email: firebaseUser.email,
  displayName: firebaseUser.displayName,
  emailVerified: firebaseUser.emailVerified,
});

const fetchAdminProfile = async (uid: string): Promise<AdminProfile | null> => {
  const adminRef = doc(db, 'admins', uid);
  const adminSnap = await getDoc(adminRef);

  if (!adminSnap.exists()) {
    return null;
  }

  const data = adminSnap.data();

  return {
    uid,
    email: typeof data.email === 'string' ? data.email : '',
    displayName: typeof data.displayName === 'string' ? data.displayName : '管理者',
  };
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  adminProfile: null,
  isLoading: true,
  isInitialized: false,
  error: null,

  syncFromFirebaseUser: async (firebaseUser) => {
    if (!firebaseUser) {
      set({
        user: null,
        adminProfile: null,
        isLoading: false,
        isInitialized: true,
      });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const adminProfile = await fetchAdminProfile(firebaseUser.uid);
      if (!adminProfile) {
        await firebaseSignOut(auth);
        set({
          user: null,
          adminProfile: null,
          isLoading: false,
          isInitialized: true,
          error: '管理者権限がありません',
        });
        return;
      }

      set({
        user: mapFirebaseUser(firebaseUser),
        adminProfile,
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (error) {
      console.error(AUTH_ERROR_MESSAGE, error);
      set({
        user: null,
        adminProfile: null,
        isLoading: false,
        isInitialized: true,
        error: AUTH_ERROR_MESSAGE,
      });
    }
  },

  signIn: async (email, password) => {
    set({ isLoading: true, error: null });

    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const adminProfile = await fetchAdminProfile(credential.user.uid);

      if (!adminProfile) {
        await firebaseSignOut(auth);
        throw new Error('管理者権限がありません');
      }

      set({
        user: mapFirebaseUser(credential.user),
        adminProfile,
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (error: unknown) {
      const message = getFirebaseErrorMessage(error);
      set({
        user: null,
        adminProfile: null,
        isLoading: false,
        isInitialized: true,
        error: message,
      });
      throw new Error(message);
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true, error: null });
      await firebaseSignOut(auth);
    } finally {
      set({
        user: null,
        adminProfile: null,
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));

function getFirebaseErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message === '管理者権限がありません') {
    return error.message;
  }

  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;

    if (typeof code === 'string') {
      switch (code) {
        case 'auth/invalid-email':
          return 'メールアドレスの形式が正しくありません';
        case 'auth/user-disabled':
          return 'このアカウントは停止されています';
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          return 'メールアドレスまたはパスワードが正しくありません';
        case 'auth/too-many-requests':
          return '試行回数が多すぎます。しばらく待ってから再試行してください';
        default:
          return AUTH_ERROR_MESSAGE;
      }
    }
  }

  return AUTH_ERROR_MESSAGE;
}
